/**
 * BF2026 pledge DEV-ONLY harness — a fake giving-api for the pledge routes.
 *
 * This lets the whole pledge flow be clicked through on web, a simulator or an
 * emulator in any state, without a test member in that state on staging:
 * `building-fund-pledge-api.js` routes each call here while the harness is on.
 *
 * The fake applies giving-api's own rules (giving-api's
 * docs/contracts/bf2026-frontend-contract.md and src/features/pledge/) —
 * eligibility, ALREADY_CONFIRMED, Pending for a youth over the cap, zero amounts
 * read back as null, the `{ data }` envelope, every failure as a 400 with its text
 * in `errors[0]`, and the couple `secondary` shape.
 *
 * It also fakes My BF Fulfilment's two reads (GET /bf/fulfilment and
 * GET /pledges/get-pledge-year) from the same pledge, with the BF gifts picked on
 * /bf-pledge-states, allocated by giving-api's own rule (allocate-fulfilment.ts).
 *
 * Inert unless switched on from /bf-pledge-states (localStorage), and never active
 * in a production build. Mirrors src/utils/egiro-harness.js. All data here is
 * placeholder data.
 */

import {
  BF_PLEDGE_API_MESSAGES,
  BF_PLEDGE_MONTHS,
  BF_PLEDGE_YEAR,
  BF_SOW_MAX_LENGTH,
  BF_YOUTH_MONTHLY_CAP,
} from '@/constants/MyGiving/building-fund-pledge';

const BF_PLEDGE_HARNESS_KEY = 'hogc_bf_pledge_harness';

export const API_ERRORS = BF_PLEDGE_API_MESSAGES;

const MONTH_KEYS = [1, 2, 3, 4, 5].map(n => `month_${n}_commitment`);
const MAX_COMMITMENT = 10 ** 8 - 1;

// ── Fixture pledges (placeholder data) ──────────────────────────────────────

// `oneTime` makes it a one-time pledge: `bf` holds that amount alone.
const body = ({
  months = [],
  oneTime = null,
  sow = null,
  isYouth = false,
  partner = null,
} = {}) => {
  const bf =
    oneTime === null
      ? Object.fromEntries(MONTH_KEYS.map((key, i) => [key, months[i] ?? 0]))
      : { one_time_commitment: oneTime };
  const amounts = oneTime === null ? months : [oneTime];
  const youthExceeded = isYouth && amounts.some(amount => amount > BF_YOUTH_MONTHLY_CAP);
  return {
    year: BF_PLEDGE_YEAR,
    sabbatical: false,
    help: false,
    tithe: false,
    tithe_continue: false,
    is_youth: isYouth,
    youth_exceeded: youthExceeded,
    bf,
    sow_pledge: sow,
    // A made-up personal ID in the shape the form builds (last 4 + DDMM).
    couplePledge: partner ? { partner_name: partner, partner_personal_id: '000A0101' } : null,
  };
};

const FIVE_MONTHS = [300, 300, 300, 300, 300];
const SOW = 'Part of my year-end bonus';

const record = (pledgeBody, overrides = {}) => ({
  body: pledgeBody,
  status: 'Pledged',
  couple_pledge_status: pledgeBody.couplePledge ? 'Pending' : 'NA',
  full_name: 'Member A',
  updatedAt: '2026-10-01T02:00:00.000Z',
  ...overrides,
});

/**
 * The member's starting state. Each one says what the screens should do with it,
 * so a wrong screen is legible from the console without reading the code.
 */
export const BF_PLEDGE_SCENARIOS = [
  {
    key: 'fresh',
    title: 'No pledge yet',
    expected:
      'The banner opens the form. Continue opens Confirm, and the slider saves it. Done ends in Give now; after a one-time pledge Give now opens with the amount filled in.',
    backend: {},
  },
  {
    key: 'pledged',
    title: 'Pledged — months and sow',
    expected: 'The banner opens View. The form redirects to View.',
    backend: { pledge: record(body({ months: FIVE_MONTHS, sow: SOW })) },
  },
  {
    key: 'pledged_months',
    title: 'Pledged — months only',
    expected: 'As above; View shows the months card, no sow card.',
    backend: { pledge: record(body({ months: [500, 500, 500, 0, 0] })) },
  },
  {
    key: 'pledged_sow',
    title: 'Pledged — sow only',
    expected: 'As above; View shows the sow card, no months card.',
    backend: { pledge: record(body({ sow: SOW })) },
  },
  {
    key: 'pledged_one_time',
    title: 'Pledged — one-time and sow',
    expected: 'View shows the one-time card and the sow card, then Give now.',
    backend: { pledge: record(body({ oneTime: 1000, sow: SOW })) },
  },
  {
    key: 'pledged_one_time_only',
    title: 'Pledged — one-time only',
    expected:
      'As above with the one-time card alone; My BF Fulfilment shows F4b: the ring and the Building Fund card, no sow card.',
    backend: { pledge: record(body({ oneTime: 1000 })) },
  },
  {
    key: 'pledged_one_time_couple',
    title: 'Pledged — one-time as a couple',
    expected: 'View shows the one-time card and the couple card, no months or sow card.',
    backend: { pledge: record(body({ oneTime: 1000, partner: 'Partner B' })) },
  },
  {
    key: 'youth',
    title: 'Youth pledge over $200',
    // giving-api saves it as Pending; Accounts checks the parental approval off-app.
    // A pledge already saved, so Confirm and Done cannot open: to see a youth's, pick
    // "No pledge yet", tick the youth box and continue.
    expected: 'View shows no pledge cards (a youth after D). No pending state.',
    backend: {
      pledge: record(body({ months: [250, 250, 250, 250, 250], isYouth: true }), {
        status: 'Pending',
      }),
    },
  },
  {
    key: 'couple',
    title: 'Couple pledge',
    // Saved with couple_pledge_status Pending until Accounts verifies; Verified looks the same.
    expected: 'View shows the partner on the couple card. No pending state.',
    backend: { pledge: record(body({ months: FIVE_MONTHS, partner: 'Partner B' })) },
  },
  {
    key: 'couple_secondary',
    title: 'Spouse pledged for both',
    expected: 'Not eligible. View names the spouse as the partner.',
    backend: {
      spousePledge: record(body({ months: FIVE_MONTHS, partner: 'Member A' }), {
        couple_pledge_status: 'Verified',
        full_name: 'Partner B',
      }),
    },
  },
  {
    key: 'closed',
    title: 'Campaign closed',
    expected: 'Not eligible and no pledge: the form shows its closed state.',
    // giving-api has no campaign window yet (§4 gap); this fakes the answer one would give.
    backend: { closed: true },
  },
];

/** Canned answers that replace the fake's normal behaviour, per call type. */
export const BF_PLEDGE_OUTCOMES = {
  submit: [
    { key: 'ok', label: 'Normal' },
    { key: 'already_confirmed', label: '400 already pledged' },
    { key: 'validation', label: '400 validation' },
    { key: 'server_error', label: 'Server fault (400)' },
    { key: 'network', label: 'No network' },
  ],
  load: [
    { key: 'ok', label: 'Normal' },
    { key: 'server_error', label: 'Server fault (400)' },
    { key: 'network', label: 'No network' },
  ],
};

export const BF_PLEDGE_LATENCIES = [
  { ms: 0, label: 'None' },
  { ms: 1500, label: '1.5 s' },
  { ms: 4000, label: '4 s' },
];

// The member's Building Fund gifts for 2026, as shares of what they pledged (or of
// $1,500 when they pledged no amount, e.g. sow only). Dated across the campaign.
const GIFT_DATES = [
  '2026-10-05',
  '2026-11-02',
  '2026-12-07',
  '2027-01-04',
  '2027-02-01',
  '2027-02-15',
];
const GIFT_BASE_WITHOUT_PLEDGE = 1500;
export const BF_FULFILMENT_GIFTS = [
  { key: 'none', label: 'None', shares: [] },
  { key: 'part', label: 'Part-way (60%)', shares: [0.2, 0.2, 0.2] },
  { key: 'full', label: 'Exactly 100%', shares: [0.2, 0.2, 0.2, 0.2, 0.2] },
  { key: 'over', label: 'Over (130%)', shares: [0.2, 0.2, 0.2, 0.2, 0.2, 0.3] },
];

// ── Persistence ─────────────────────────────────────────────────────────────

const emptyBackend = () => ({ pledge: null, spousePledge: null, closed: false });

const defaultState = () => ({
  enabled: false,
  scenario: 'fresh',
  backend: emptyBackend(),
  outcomes: { submit: 'ok', load: 'ok' },
  latencyMs: 0,
  gifts: 'none',
});

function readState() {
  try {
    const raw = window.localStorage.getItem(BF_PLEDGE_HARNESS_KEY);
    if (!raw) return defaultState();
    const saved = JSON.parse(raw);
    const base = defaultState();
    return {
      ...base,
      ...saved,
      backend: { ...base.backend, ...saved.backend },
      outcomes: { ...base.outcomes, ...saved.outcomes },
    };
  } catch {
    return defaultState();
  }
}

function writeState(next) {
  try {
    window.localStorage.setItem(BF_PLEDGE_HARNESS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — harness simply stays off */
  }
  return next;
}

/** True only when switched on AND not a production build. */
export function isBfPledgeHarnessActive() {
  if (process.env.VUE_APP_NODE_ENV === 'production') return false;
  return readState().enabled === true;
}

export function getBfPledgeHarnessState() {
  return readState();
}

export function setBfPledgeHarness(patch) {
  return writeState({ ...readState(), ...patch });
}

/** Switch the harness on with a scenario's backend, replacing whatever it held. */
export function applyBfPledgeScenario(key) {
  const scenario = BF_PLEDGE_SCENARIOS.find(s => s.key === key) || BF_PLEDGE_SCENARIOS[0];
  const backend = { ...emptyBackend(), ...JSON.parse(JSON.stringify(scenario.backend)) };
  return writeState({ ...readState(), enabled: true, scenario: scenario.key, backend });
}

export function setBfPledgeOutcome(kind, key) {
  const state = readState();
  return writeState({ ...state, outcomes: { ...state.outcomes, [kind]: key } });
}

export function setBfFulfilmentGifts(key) {
  return writeState({ ...readState(), gifts: key });
}

export function resetBfPledgeHarness() {
  return writeState(defaultState());
}

// ── The fake API ────────────────────────────────────────────────────────────

// Errors shaped like axios's, so the adapter reads real and fake failures alike.
// giving-api's LegacyExceptionFilter answers every failure on these routes as a
// 400 with one message, a validation list joined by commas.
const httpError = message => {
  const error = new Error('Request failed with status code 400');
  error.response = { status: 400, data: { data: {}, status: 'error', errors: [message] } };
  return error;
};

// Placeholder text in the shape of a Prisma failure, which is what reaches errors[0].
export const SERVER_FAULT = "Can't reach database server at `db.example:3306`";

const networkError = () => {
  const error = new Error('Network Error');
  error.request = {};
  return error;
};

const forced = (kind, outcome) => {
  if (outcome === 'network') return networkError();
  // A server fault is a 400 too, carrying the exception's own text.
  if (outcome === 'server_error') return httpError(SERVER_FAULT);
  if (kind === 'submit' && outcome === 'already_confirmed') {
    return httpError(API_ERRORS.ALREADY_CONFIRMED);
  }
  if (kind === 'submit' && outcome === 'validation') {
    return httpError('bf.month_1_commitment must not be greater than 99999999');
  }
  return null;
};

async function respond(kind, handler) {
  const state = readState();
  if (state.latencyMs) await new Promise(resolve => setTimeout(resolve, state.latencyMs));
  const error = forced(kind, state.outcomes[kind]);
  if (error) throw error;
  return handler(state);
}

const isAmount = value =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= 0 &&
  value <= MAX_COMMITMENT &&
  Math.round(value * 100) === value * 100;

// giving-api's `MEMBER_DETAIL_MAX_LENGTH`: the pledges columns are VARCHAR(255).
const MEMBER_DETAIL_KEYS = ['full_name', 'contact_number', 'connect_group'];
const MEMBER_DETAIL_MAX_LENGTH = 255;

const TOP_KEYS = [
  'year',
  'sabbatical',
  'help',
  'tithe',
  'tithe_continue',
  'is_youth',
  'youth_exceeded',
  'from_youth_page',
  'bf',
  'hcs',
  'gf',
  'couplePledge',
  'sow_pledge',
  // "I'm giving for the first time", a youth's only (giving-api #210).
  'first_four_digit_pledge',
  // "Confirm personal details", which the form always sends (#1857).
  ...MEMBER_DETAIL_KEYS,
];
const BF_KEYS = ['one_time_commitment', ...MONTH_KEYS];
const REQUIRED_BOOLEANS = ['sabbatical', 'help', 'tithe', 'tithe_continue', 'is_youth'];

/**
 * class-validator's checks on giving-api's PledgeBodyDto (`whitelist` +
 * `forbidNonWhitelisted`, no `transform`). Returns its messages.
 */
export function validatePledgeBody(pledgeBody) {
  const messages = [];
  Object.keys(pledgeBody).forEach(key => {
    if (!TOP_KEYS.includes(key)) messages.push(`property ${key} should not exist`);
  });
  REQUIRED_BOOLEANS.forEach(key => {
    if (typeof pledgeBody[key] !== 'boolean') messages.push(`${key} must be a boolean value`);
  });
  const { bf, couplePledge, sow_pledge: sow } = pledgeBody;
  if (bf) {
    Object.keys(bf).forEach(key => {
      if (!BF_KEYS.includes(key)) messages.push(`bf.property ${key} should not exist`);
    });
    [...MONTH_KEYS, 'one_time_commitment'].forEach(key => {
      if (bf[key] !== undefined && bf[key] !== null && !isAmount(bf[key])) {
        messages.push(`bf.${key} must be a number from 0 to ${MAX_COMMITMENT} with 2 decimals`);
      }
    });
  }
  const firstTime = pledgeBody.first_four_digit_pledge;
  if (firstTime !== undefined && typeof firstTime !== 'boolean') {
    messages.push('first_four_digit_pledge must be a boolean value');
  }
  MEMBER_DETAIL_KEYS.forEach(key => {
    const value = pledgeBody[key];
    if (value === undefined) return;
    if (typeof value !== 'string') messages.push(`${key} must be a string`);
    else if (value.length > MEMBER_DETAIL_MAX_LENGTH) {
      messages.push(
        `${key} must be shorter than or equal to ${MEMBER_DETAIL_MAX_LENGTH} characters`,
      );
    }
  });
  if (sow !== undefined && sow !== null) {
    if (typeof sow !== 'string') messages.push('sow_pledge must be a string');
    else if (sow.length > BF_SOW_MAX_LENGTH) {
      messages.push(`sow_pledge must be shorter than or equal to ${BF_SOW_MAX_LENGTH} characters`);
    }
  }
  if (couplePledge) {
    const name = couplePledge.partner_name;
    if (typeof name !== 'string' || !name.trim()) {
      messages.push('couplePledge.partner_name should not be empty');
    }
  }
  return messages;
}

// `Number(x) || null`, giving-api's read conversion: 0 comes back as null.
const readAmount = value => Number(value ?? 0) || null;

function readCommitment(bf = {}) {
  return {
    one_time_commitment: readAmount(bf.one_time_commitment),
    ...Object.fromEntries(MONTH_KEYS.map(key => [key, readAmount(bf[key])])),
  };
}

// A stored record in the shape GET /pledges/confirm returns.
function readPledge(stored, { secondary = false } = {}) {
  const { body: pledgeBody } = stored;
  const couple = stored.couple_pledge_status !== 'NA' && pledgeBody.couplePledge;
  return {
    sabbatical: !!pledgeBody.sabbatical,
    help: !!pledgeBody.help,
    tithe: !!pledgeBody.tithe,
    tithe_continue: !!pledgeBody.tithe_continue,
    // giving-api's spouse view (CouplePledge in pledge-couple.service.ts) carries
    // no youth fields, so a spouse never sees the youth line.
    ...(secondary
      ? {}
      : {
          is_youth: !!pledgeBody.is_youth,
          youth_exceeded: !!pledgeBody.youth_exceeded && !!pledgeBody.is_youth,
        }),
    personalId: secondary ? 'P-SPOUSE' : 'P-MEMBER',
    status: stored.status,
    zone: null,
    type: 'Digital',
    updatedAt: stored.updatedAt,
    ...(secondary ? { full_name: stored.full_name } : {}),
    bf: readCommitment(pledgeBody.bf),
    sow_pledge: pledgeBody.sow_pledge?.trim() || null,
    // As sent, null when not asked; always null on a spouse's read (the primary's own).
    first_four_digit_pledge: secondary ? null : (pledgeBody.first_four_digit_pledge ?? null),
    ...(couple
      ? {
          couplePledge: {
            partner_name: pledgeBody.couplePledge.partner_name,
            partner_personal_id: pledgeBody.couplePledge.partner_personal_id ?? null,
            couple_pledge_status: stored.couple_pledge_status,
            partner_tithe: true,
          },
        }
      : {}),
    secondary,
  };
}

const eligible = backend => !backend.closed && !backend.pledge && !backend.spousePledge;

// ── My BF Fulfilment ────────────────────────────────────────────────────────

const toCents = amount => Math.round(amount * 100);

/**
 * giving-api's allocateFulfilment (src/features/bf-fulfilment/allocate-fulfilment.ts),
 * line for line: gifts in date order fill the pledge, then — with a sow — the rest
 * is sow, the crossing gift split; a sow alone takes every gift; nothing pledged
 * leaves them "given". In cents, so a split never drifts.
 */
export function allocateFulfilment({ pledgeTotal, hasSow, gifts }) {
  const totalCents = Math.max(0, toCents(pledgeTotal));
  const hasPledge = totalCents > 0;
  const bucket = () => ({ cents: 0, gifts: [] });
  const add = (into, date, cents) => {
    if (cents <= 0) return;
    into.cents += cents;
    into.gifts.push({ date, amount: cents / 100 });
  };
  const pledge = bucket();
  const sow = bucket();
  const given = bucket();

  gifts
    .map(gift => ({ date: gift.date, cents: toCents(gift.amount) }))
    .filter(gift => gift.cents > 0)
    .sort((a, b) => a.date.localeCompare(b.date))
    .forEach(({ date, cents }) => {
      if (hasPledge && !hasSow) add(pledge, date, cents);
      else if (hasPledge && hasSow) {
        const toPledge = Math.min(cents, Math.max(0, totalCents - pledge.cents));
        add(pledge, date, toPledge);
        add(sow, date, cents - toPledge);
      } else if (hasSow) add(sow, date, cents);
      else add(given, date, cents);
    });

  const view = ({ cents, gifts: list }) => ({ amount: cents / 100, gifts: list });
  return {
    pledge: hasPledge
      ? { ...view(pledge), percentage: Math.floor((pledge.cents * 100) / totalCents) }
      : null,
    sow: hasSow ? view(sow) : null,
    given: hasPledge || hasSow ? null : view(given),
  };
}

// The gifts the console's "BF given" setting stands for, against this pledge total.
function harnessGifts(key, pledgeTotal) {
  const option = BF_FULFILMENT_GIFTS.find(o => o.key === key) || BF_FULFILMENT_GIFTS[0];
  const base = pledgeTotal > 0 ? pledgeTotal : GIFT_BASE_WITHOUT_PLEDGE;
  return option.shares.map((share, i) => ({
    date: GIFT_DATES[i],
    amount: Math.round(base * share * 100) / 100,
  }));
}

// GET /bf/fulfilment's BfFulfilmentView, built from the pledge GET /pledges/confirm
// would return (a verified spouse's included). Only the campaign year has any.
function fulfilmentView(state, year) {
  const { backend } = state;
  const isCampaign = year === BF_PLEDGE_YEAR;
  const stored = backend.spousePledge || backend.pledge;
  const pledge =
    stored && isCampaign ? readPledge(stored, { secondary: !!backend.spousePledge }) : null;
  const months = pledge
    ? BF_PLEDGE_MONTHS.map(({ key, period }, i) => ({
        month: i + 1,
        period,
        amount: Number(pledge.bf[key] ?? 0),
      }))
    : [];
  const oneTime = Number(pledge?.bf.one_time_commitment ?? 0);
  const pledgeTotal = months.reduce((sum, month) => sum + month.amount, 0) + oneTime;
  const sowText = pledge?.sow_pledge || null;
  const gifts = isCampaign ? harnessGifts(state.gifts, pledgeTotal) : [];

  return {
    year,
    pledge:
      pledgeTotal > 0
        ? { total: pledgeTotal, oneTime: oneTime > 0 ? oneTime : null, months }
        : null,
    sow: sowText ? { text: sowText } : null,
    fulfilment: allocateFulfilment({ pledgeTotal, hasSow: !!sowText, gifts }),
  };
}

// Each returns what the real route would, envelope included.
export const fakeGivingApi = {
  fetchPledge() {
    return respond('load', ({ backend }) => {
      if (backend.spousePledge)
        return { data: readPledge(backend.spousePledge, { secondary: true }) };
      return { data: backend.pledge ? readPledge(backend.pledge) : null };
    });
  },

  fetchEligibility() {
    return respond('load', ({ backend }) => ({ data: { isEligible: eligible(backend) } }));
  },

  // Plain JSON, like the real route: no `{ data }` envelope of its own.
  fetchFulfilment(year) {
    return respond('load', state => ({ data: fulfilmentView(state, Number(year)) }));
  },

  // The campaign year once there is a pledge to show (a verified spouse's counts).
  fetchPledgeYears() {
    return respond('load', ({ backend }) => ({
      data: backend.pledge || backend.spousePledge ? [BF_PLEDGE_YEAR] : [],
    }));
  },

  submitPledge(pledgeBody) {
    return respond('submit', state => {
      const messages = validatePledgeBody(pledgeBody);
      if (messages.length) throw httpError(messages.join(', '));
      if (!eligible(state.backend)) throw httpError(API_ERRORS.ALREADY_CONFIRMED);

      const pending = pledgeBody.is_youth && pledgeBody.youth_exceeded;
      const pledge = record(pledgeBody, {
        status: pending ? 'Pending' : 'Pledged',
        updatedAt: new Date().toISOString(),
      });
      writeState({ ...state, backend: { ...state.backend, pledge } });
      return { data: undefined };
    });
  },
};

// ── FAST gift results ───────────────────────────────────────────────────────

// The status page's FAST gifts, by transaction ID. A Building Fund gift that went
// through gets the BF2026 "FAST - Submitted" screen; everything else keeps the
// generic status screens, which these are here to show alongside it.
const FAST_ID_PREFIX = 'bf-harness-';
const FAST_PENDING_POLLS = 3;

export const BF_FAST_TRANSACTIONS = [
  {
    id: `${FAST_ID_PREFIX}success`,
    title: 'Building Fund · succeeded',
    expected: 'The BF2026 "FAST - Submitted" screen: $1000, this month, Building Fund 2026.',
    response: { status: 'SUCCESS', givingCategory: 'Building Fund', amount: 1000 },
  },
  {
    id: `${FAST_ID_PREFIX}pending`,
    title: 'Building Fund · queued, then succeeds',
    expected: `The generic pending screen, then the BF2026 screen after ${FAST_PENDING_POLLS} polls (~${FAST_PENDING_POLLS * 2} s).`,
    response: { status: 'PENDING', givingCategory: 'Building Fund', amount: 250.5 },
    succeedsAfter: FAST_PENDING_POLLS,
  },
  {
    id: `${FAST_ID_PREFIX}failed`,
    title: 'Building Fund · failed',
    expected: 'The generic failed screen ("That took longer than expected.").',
    response: {
      status: 'FAILED',
      givingCategory: 'Building Fund',
      amount: 1000,
      errorCode: 'TIMEOUT_EXPIRED',
    },
  },
  {
    id: `${FAST_ID_PREFIX}tithe`,
    title: 'Tithe · succeeded',
    expected: 'The generic "Successful" screen — only Building Fund gets the BF2026 one.',
    response: { status: 'SUCCESS', givingCategory: 'Tithe', amount: 1000 },
  },
];

// Polls seen per pending transaction, so it can succeed after a few. Module state,
// not storage: reopening the page starts it pending again.
const fastPolls = new Map();

/** True when the harness is on and `transactionId` is one of its FAST gifts. */
export function isBfFastHarnessTransaction(transactionId) {
  // The prefix first, so a real transaction's polls never read storage.
  return (
    typeof transactionId === 'string' &&
    transactionId.startsWith(FAST_ID_PREFIX) &&
    isBfPledgeHarnessActive()
  );
}

/** Start a queued gift over, as the console does each time it opens one. */
export function resetBfFastPolls() {
  fastPolls.clear();
}

/**
 * What `GET /e-giro/fast/status/:id` answers for a harness gift, shaped as the
 * axios response `myGiving/checkFastTransactionStatus` returns. Placeholder data.
 */
export async function fakeFastStatus(transactionId) {
  const transaction = BF_FAST_TRANSACTIONS.find(t => t.id === transactionId);
  if (!transaction) throw httpError(`Transaction ${transactionId} not found`);

  const { latencyMs } = readState();
  if (latencyMs) await new Promise(resolve => setTimeout(resolve, latencyMs));

  const polls = (fastPolls.get(transactionId) || 0) + 1;
  fastPolls.set(transactionId, polls);
  const succeeded = transaction.succeedsAfter && polls > transaction.succeedsAfter;

  return {
    data: {
      transactionId,
      frequency: 'One-Time',
      monthOfGiving: new Date().toLocaleString('en-US', { month: 'long' }),
      bankCode: 'DBSSSGSGXXX',
      bankAccountNo: 'xxxx-xxxx-0000',
      createdAt: new Date().toISOString(),
      ...transaction.response,
      ...(succeeded && { status: 'SUCCESS' }),
    },
  };
}
